import React, { useState, useMemo } from 'react';

function ShopConfigTable({ shopConfigs, onShopChange, onDeleteLink, onToggleActive, onTestSelector, loading, testingSelectors, testResults }) {
  const [sortField, setSortField] = useState('shopName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterActive, setFilterActive] = useState(null);

  const sortedAndFilteredShops = useMemo(() => {
    let shops = [...shopConfigs];

    if (filterActive !== null) {
      shops = shops.filter(s => s.active === filterActive);
    }

    shops.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'shopName':
          comparison = (a.shopName || '').toLowerCase().localeCompare((b.shopName || '').toLowerCase());
          break;

        case 'productUrl':
          const hasUrlA = a.productUrl ? 1 : 0;
          const hasUrlB = b.productUrl ? 1 : 0;
          comparison = hasUrlB - hasUrlA;
          break;

        case 'price':
          const testA = testResults[a.storeId];
          const testB = testResults[b.storeId];
          
          const priceA = testA?.found && testA?.price ? parseFloat(testA.price) : null;
          const priceB = testB?.found && testB?.price ? parseFloat(testB.price) : null;
          
          if (priceA === null && priceB === null) comparison = 0;
          else if (priceA === null) comparison = 1;
          else if (priceB === null) comparison = -1;
          else comparison = priceA - priceB;
          break;

        case 'active':
          comparison = (b.active ? 1 : 0) - (a.active ? 1 : 0);
          break;

        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return shops;
  }, [shopConfigs, sortField, sortDirection, filterActive, testResults]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortClass = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? 'sorted-asc' : 'sorted-desc';
  };

  const getStatusBadges = (shop) => {
    const badges = [];

    // Aktiv/Inaktiv Badge
    badges.push(
      <span key="active" className={`status-badge-small ${shop.active ? 'active' : 'inactive'}`}>
        {shop.active ? '✓ Aktiv' : '✕ Inaktiv'}
      </span>
    );

    // Link Badge
    badges.push(
      <span
        key="link"
        className={`status-badge-small ${shop.productUrl ? 'link-found' : 'link-missing'}`}
      >
        {shop.productUrl ? '🔗 Link' : '❌ Kein Link'}
      </span>
    );

    // Preis Badge
    const testResult = testResults[shop.storeId];
    if (testResult?.found && testResult?.price) {
      badges.push(
        <span key="price" className="status-badge-small price-found">
          💰 Preis
        </span>
      );
    } else if (shop.productUrl) {
      badges.push(
        <span key="price" className="status-badge-small price-missing">
          ❌ Kein Preis
        </span>
      );
    }

    return badges;
  };

  return (
    <div className="shop-table-wrapper">
      <div className="shop-table-header">
        <h3>🛒 Shop-Konfigurationen ({sortedAndFilteredShops.length}/{shopConfigs.length})</h3>
        <div className="shop-sort-controls">
          <button
            type="button"
            className={`sort-button ${filterActive === null ? 'active' : ''}`}
            onClick={() => setFilterActive(null)}
          >
            Alle ({shopConfigs.length})
          </button>
          <button
            type="button"
            className={`sort-button ${filterActive === true ? 'active' : ''}`}
            onClick={() => setFilterActive(true)}
          >
            Aktiv ({shopConfigs.filter(s => s.active).length})
          </button>
          <button
            type="button"
            className={`sort-button ${filterActive === false ? 'active' : ''}`}
            onClick={() => setFilterActive(false)}
          >
            Inaktiv ({shopConfigs.filter(s => !s.active).length})
          </button>
        </div>
      </div>

      <table className="shop-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('shopName')} className={`sortable ${getSortClass('shopName')}`}>
              Shop
            </th>
            <th onClick={() => handleSort('productUrl')} className={`sortable ${getSortClass('productUrl')}`}>
              Link
            </th>
            <th onClick={() => handleSort('price')} className={`sortable ${getSortClass('price')}`}>
              Preis
            </th>
            <th>Lieferzeit</th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredShops.length === 0 ? (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
                {filterActive !== null
                  ? 'Keine Shops mit diesem Filter gefunden'
                  : 'Noch keine Shops verfügbar'}
              </td>
            </tr>
          ) : (
            sortedAndFilteredShops.map((shop) => {
              const testResult = testResults[shop.storeId];
              const hasPrice = testResult?.found && testResult?.price;

              return (
                <tr key={shop.storeId} className={!shop.active ? 'inactive' : ''}>
                  {/* Shop Name mit Logo */}
                  <td>
                    <a 
                      href={shop.productUrl || shop.storeWebsite || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shop-logo-cell"
                      style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.6rem' }}
                      onClick={(e) => {
                        if (!shop.productUrl && !shop.storeWebsite) {
                          e.preventDefault();
                        }
                      }}
                    >
                      {shop.logo && (
                        <img
                          src={shop.logo}
                          alt={shop.shopName}
                          className={`shop-logo-thumb ${!shop.active ? 'inactive-shop' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onToggleActive(shop.storeId);
                          }}
                          onError={(e) => e.target.style.display = 'none'}
                          style={{ cursor: 'pointer' }}
                          title={shop.active ? 'Klicken zum Deaktivieren' : 'Klicken zum Aktivieren'}
                        />
                      )}
                      <span style={{ color: '#d0d0d0', fontWeight: 500 }}>{shop.shopName}</span>
                    </a>
                  </td>

                  {/* Produkt-URL Input */}
                  <td>
                    <div className="url-input-cell">
                      <input
                        type="url"
                        value={shop.productUrl || ''}
                        onChange={(e) => onShopChange(shop.storeId, 'productUrl', e.target.value)}
                        placeholder="https://..."
                        disabled={loading}
                        className="table-url-input"
                      />
                      <button
                        className="btn-test-url"
                        onClick={() => onTestSelector(shop.storeId)}
                        disabled={loading || testingSelectors[shop.storeId] || !shop.productUrl}
                        title="Preis suchen"
                      >
                        {testingSelectors[shop.storeId] ? '⏳' : '🔍'}
                      </button>
                      <button
                        className="btn-delete-link"
                        onClick={() => onDeleteLink(shop.storeId)}
                        disabled={loading || !shop.productUrl}
                        title="Link und Preis löschen"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>

                  {/* Preis */}
                  <td className={`price-cell ${!hasPrice ? 'empty' : ''}`}>
                    {testingSelectors[shop.storeId] ? (
                      <div style={{ color: '#00d4ff', fontSize: '0.85rem' }}>Suche...</div>
                    ) : hasPrice ? (
                      <>
                        <div style={{ fontWeight: 600, color: '#ffd700' }}>{testResult.price.toFixed(2)}€</div>
                        {shop.lastScrapedAt && (
                          <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '0.2rem' }}>
                            {new Date(shop.lastScrapedAt).toLocaleDateString('de-DE')}
                          </div>
                        )}
                      </>
                    ) : testResult?.message ? (
                      <div style={{ color: '#ff6666', fontSize: '0.8rem' }}>Fehler</div>
                    ) : (
                      <span style={{ color: '#888' }}>—</span>
                    )}
                  </td>

                  {/* Lieferzeit */}
                  <td className="delivery-time-cell">
                    {shop.deliveryTime ? (
                      <span className="delivery-time-text">{shop.deliveryTime}</span>
                    ) : (
                      <span style={{ color: '#666', fontSize: '0.85rem' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default ShopConfigTable;
